import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../context/ThemeContext';
import Section from '../components/ui/Section';
import SectionHeader from '../components/ui/SectionHeader';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import GalleryModal from '../components/GalleryModal';

const CaseStudies = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [selectedProject, setSelectedProject] = useState(null);

  // Define projects with their images
  const projects = [

    {
      id: 'faid',
      title: t('caseStudies.faid.title'),
      industry: t('caseStudies.faid.industry'),
      description: t('caseStudies.faid.description'),
      outcomes: t('caseStudies.faid.outcomes', { returnObjects: true }),
      gradient: 'from-afsc-gold/20 to-afsc-gold-deep/20',
      previewImage: '/projects/faid/Apple iPhone 16 Pro Max Screenshot 1.png',
      images: [
        '/projects/faid/Apple iPhone 16 Pro Max Screenshot 1.png',
        '/projects/faid/Apple iPhone 16 Pro Max Screenshot 2.png',
        '/projects/faid/Apple iPhone 16 Pro Max Screenshot 3.png',
        '/projects/faid/Apple iPhone 16 Pro Max Screenshot 4.png',
      ],
    },
 
  ];

  const handleViewFull = (project) => {
    setSelectedProject(project);
  };

  const handleCloseModal = () => {
    setSelectedProject(null);
  };

  return (
    <>
      <Section id="case-studies" withFlyingElements={true}>
        <SectionHeader
          title={t('caseStudies.title')}
          subtitle={t('caseStudies.subtitle')}
          withDivider={true}
        />
        <div className="grid md:grid-cols-3 gap-6">
          {projects.map((project, index) => (
            <motion.div
              key={project.id}
              initial={{ opacity: 0, y: 30, filter: 'blur(10px)' }}
              whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.6 }}
            >
              <Card className={`h-full bg-gradient-to-br ${isDark ? 'bg-white/5' : 'bg-white/60'} backdrop-blur-sm border ${isDark ? 'border-afsc-soft-gray/20' : 'border-afsc-charcoal/10'} hover:border-afsc-gold overflow-hidden flex flex-col`}>
                {/* Preview Image */}
                <div className="relative h-64 -mx-6 -mt-6 mb-6 overflow-hidden bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                  <img
                    src={project.previewImage}
                    alt={project.title}
                    className="w-full h-full object-contain"
                  />
                  <div className={`absolute inset-0 bg-gradient-to-br ${project.gradient} opacity-30 pointer-events-none`} />
                </div>
                
                <div className="px-6 flex-1 flex flex-col">
                  <span className="text-xs text-afsc-gold uppercase tracking-wider font-semibold">
                    {project.industry}
                  </span>
                  <h3 className={`text-2xl font-heading font-bold mb-3 mt-2 ${isDark ? 'text-white' : 'text-afsc-charcoal'}`}>
                    {project.title}
                  </h3>
                  <p className={`mb-6 ${isDark ? 'text-white/80' : 'text-afsc-charcoal/70'}`}>
                    {project.description}
                  </p>
                  <ul className="space-y-2 mb-6 flex-1">
                    {project.outcomes.map((outcome, i) => (
                      <li key={i} className={`flex items-start text-sm ${isDark ? 'text-white/70' : 'text-afsc-charcoal/70'}`}>
                        <span className="text-afsc-gold mr-2">✓</span>
                        {outcome}
                      </li>
                    ))}
                  </ul>
                  <Button
                    variant="secondary"
                    className="w-full"
                    onClick={() => handleViewFull(project)}
                  >
                    {t('caseStudies.viewCaseStudy')}
                  </Button>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* Gallery Modal */}
      {selectedProject && (
        <GalleryModal
          isOpen={!!selectedProject}
          onClose={handleCloseModal}
          images={selectedProject.images}
          title={selectedProject.title}
        />
      )}
    </>
  );
};

export default CaseStudies;

